/* eslint-disable @typescript-eslint/no-explicit-any */
// import apiLinks from "@/src/apiLinks";
import apiRoutes from "@/src/apiRoutes";
import { useAppDispatch } from "@/src/app/hooks";
import { Button } from "@/src/components/Button/Button";
import InputComponent from "@/src/components/Input/Input";
import { Modal } from "@/src/components/Modal/Modal";
import { textVariants } from "@/src/components/Text/Text";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/src/components/shad/card";
import { signOut } from "@/src/features/userSlice";
import useUrlState from "@/src/hooks/use-url-state";
import api from "@/src/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import _ from "lodash";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

const changeState = z
  .object({
    old_password: z.string(),
    new_password: z.string().min(6, {
      message: "Your new password should be a minimum of 6 letters",
    }),
    confirm: z.string(),
    code: z.string().optional(),
  })
  .refine((data) => data.new_password === data.confirm, {
    message: "Passwords do not match",
    path: ["confirm"],
  });

const ChangePassword = () => {
  const { t } = useTranslation();
  const trans = (key: string) =>
    t(`app.settings.securityCenter.account.changePassword.${key}`);
  type resetType = z.infer<typeof changeState>;
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    // watch
  } = useForm<resetType>({
    resolver: zodResolver(changeState),
  });
  const [modal, setModal] = useUrlState("modal");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const onReset = async () => {
    const newData = _.omit(getValues(), "confirm");
    const res = await api.post(apiRoutes.passwordResetInternal, {
      ...newData,
      type: "change",
    });
    return res.data;
  };

  const { mutate, isLoading } = useMutation({
    mutationKey: ["change_password"],
    mutationFn: onReset,
    onSuccess: (data) => {
      if (data?.success) {
        toast.success(data?.info);
        dispatch(signOut());
        navigate("/");
      } else {
        toast.warning(data?.info);
      }
    },
    onError: () => {
      toast.error("Password change failed");
    },
  });

  const sendUssdOtp = async () => {
    const res = await api.post(apiRoutes.passwordResetInternal, {
      type: "request",
    });
    return res.data;
  };

  const { mutate: sendOtp, isLoading: otpLoad } = useMutation({
    mutationFn: sendUssdOtp,
    onSuccess: (data) => {
      if (data?.success) {
        setModal("open");
        toast.success(data?.info);
      } else {
        toast.warning(data?.info);
      }
    },
  });

  const submitOtp: SubmitHandler<any> = () => {
    sendOtp();
  };

  const onSubmit: SubmitHandler<resetType> = () => {
    // sendOtp();
    mutate();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{trans("title")}</CardTitle>
      </CardHeader>
      <form>
        <CardContent className="lg:w-2/3 xl:w-1/2 grid gap-3">
          <InputComponent
            {...register("old_password")}
            label={trans("modal.form.oldPassword.label")}
            type="password"
            errors={errors.old_password?.message}
          />
          <InputComponent
            {...register("new_password")}
            label={trans("modal.form.newPassword.label")}
            type="password"
            errors={errors.new_password?.message}
          />
          <InputComponent
            {...register("confirm")}
            label={trans("modal.form.confirmPassword.label")}
            type="password"
            errors={errors.confirm?.message}
          />
        </CardContent>
        <CardHeader className=" bg-back p-2 flex flex-row justify-between items-center border-t rounded-b-lg">
          <span className={textVariants({ variant: "desc", font: "medium" })}>
            {trans("modal.footerText")}
          </span>

          <Button
            variant={"primary"}
            onClick={handleSubmit(submitOtp)}
            // title="Change Password"
            loading={otpLoad}
          >
            {trans("modal.form.submitButton")}
          </Button>
        </CardHeader>
      </form>
      <Modal
        open={modal === "open"}
        setOpen={(val) => setModal(val ? undefined : "open")}
        title={trans("confirmModal.title")}
        description={trans("confirmModal.desc")}
        footer={
          <Button
            loading={isLoading}
            onClick={handleSubmit(onSubmit)}
            variant={"outline"}
          >
            {trans("confirmModal.form.submitButton")}
          </Button>
        }
        size={"sm"}
      >
        <div>
          <InputComponent
            onChange={(e) => setValue("code", e.target.value)}
            label={trans("confirmModal.form.otp.label")}
            type="number"
          />
        </div>
      </Modal>
    </Card>
  );
};

export default ChangePassword;
